import React from "react";
import { useTranslation } from "react-i18next";
import {
    Grid,
    Filter,
    useBaseAppContext,
    useResourceApi,
    useFilterContext,
    ActionButton,
    numberFormatCurrency,
    numberFormat,
    useFormContext,
} from "@programari-limit/base-react";
import MuiGrid from "@mui/material/Grid";
import {
    Badge,
    Box,
    Button,
    Divider,
    Icon,
    IconButton,
    Tooltip,
    Typography,
    darken,
} from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { suffixValueFormatter } from "utils/muiUtils";
import GridFilterField from "components/GridFilterField";
import { SpringFilterQueryBuilder } from "utils/spring-filter-query-builder";
import {
    columnesAgendaProducte,
    columnesProducte,
    columnesUsuaris,
    columnesZones,
} from "sharedAdvancedSearch/advancedSearchColumns";
import { likeAny } from "utils/springFilterUtils";
import { useNavigate } from "react-router-dom";
import { GRID_CHECKBOX_SELECTION_FIELD } from "@mui/x-data-grid";
import GridFormField from "components/GridFormField";
import CellEditComp2Reserves from "components/CellEditComp2Reserves";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";

export const getFielYellow = (mode) => "#f9f6aa";
export const getFieldHighlightRed = (mode) => "#ffe6e6";
export const getFieldHighlightOrange = (mode) => "#ffca96";

const ActionAbonarContent = ({ reservaId }) => {
    const { t } = useTranslation();
    const { data } = useFormContext();

    const columns = [
        {
            name: "client",
            flex: 1,
        },
        {
            name: "tipusClient",
            flex: 1,
        },
        {
            name: "quantitat",
            flex: 1,
        },
        {
            name: "preu",
            flex: 1,
            type: "currency",
            currencyDecimalPlaces: 2,
            formFieldProps: {
                suffix: " €",
            },
            valueFormatter: (params) => suffixValueFormatter(params),
        },
        {
            name: "importDescompte",
            flex: 1.5,
            type: "currency",
            currencyDecimalPlaces: 2,
            formFieldProps: {
                suffix: " €",
            },
            valueFormatter: (params) => suffixValueFormatter(params),
        },
        {
            name: "importLinia",
            flex: 1,
            type: "currency",
            currencyDecimalPlaces: 2,
            formFieldProps: {
                suffix: " €",
            },
            valueFormatter: (params) => suffixValueFormatter(params),
        },
        {
            name: "quantitatRestant",
            flex: 1,
        },
        {
            name: "quantitaDevolucio",
            flex: 1.25,
            minWidth: 220,
            headerName: t("operacio.action.campAbonament"),
            renderCell: (params) => (
                <CellEditComp2Reserves
                    tooltip={t("operacio.action.tooltipCampAbonament")}
                    placeholder={t("operacio.action.campAbonament")}
                    id={params.id}
                    keyVal="reservesLiniaInfo"
                    fieldKey="quantitatDevolucio"
                    max={params?.row?.quantitatRestant}
                    min={0}
                    step={0.001}
                />
            ),
        },
        {
            name: "preuDevolucio",
            flex: 1.25,
            minWidth: 250,
            headerName: t("operacio.action.campPreuAbonament"),
            renderCell: (params) => (
                <CellEditComp2Reserves
                    tooltip={t("operacio.action.tooltipCampPreuAbonament")}
                    placeholder={t("operacio.action.campPreuAbonament")}
                    id={params.id}
                    keyVal="reservesLiniaInfo"
                    fieldKey="preuDevolucio"
                    max={params?.row?.importLinia}
                    min={0}
                    step={0.001}
                />
            ),
        },
    ];

    return (
        <Box sx={{ height: "100%" }}>
            <MuiGrid container spacing={2} sx={{ my: 1 }}>
                <GridFormField
                    name="user"
                    lg={6}
                    advancedSearchColumns={columnesUsuaris}
                    advancedSearchMaxWidth="lg"
                    size="small"
                />
                <GridFormField name="tipusAbonamentForm" lg={3} size="small" />
                <GridFormField name="pagamentTipus" lg={3} size="small" />
                <GridFormField
                    name="importTargeta"
                    lg={3}
                    size="small"
                    disabled={data?.pagamentTipus === "EFECTIU"}
                    suffix={" €"}
                />
                <GridFormField
                    name="importEfectiu"
                    lg={3}
                    size="small"
                    disabled={data?.pagamentTipus === "TARGETA"}
                    suffix={" €"}
                />
                <GridFormField
                    name="importAbonament"
                    lg={3}
                    size="small"
                    suffix={" €"}
                    disabled
                />
                <GridFormField
                    name="importARetornar"
                    lg={3}
                    size="small"
                    suffix={" €"}
                    disabled
                />
            </MuiGrid>
            {data?.tipusAbonamentForm === "ABONAMENT_PARCIAL" && (
                <Grid
                    title={t("reservesLinia.title")}
                    resourceName="reservaLinia"
                    columns={columns}
                    density="compact"
                    readOnly
                    inlineEditable={false}
                    fixedFilter={
                        reservaId ? `reserva.id : '${reservaId}'` : undefined
                    }
                    height={300}
                    // initialState={{
                    //     pinnedColumns: {
                    //         right: ["quantitaDevolucio", "preuDevolucio"],
                    //     },
                    // }}
                />
            )}
        </Box>
    );
};

const BotoExportacio = (props) => {
    const { resourceName, exportData, exportCallbacks } = props;
    const { t } = useTranslation();
    const {
        apiInitialized,
        apiResource,
        exec: apiExec,
        execHref: apiExecHref,
        execDownload: apiExecDownload,
    } = useResourceApi(resourceName);

    const download = () => {
        apiExecDownload("export", {
            data: exportData,
            callbacks: exportCallbacks,
        });
    };

    if (!apiInitialized) return;

    return (
        <Tooltip title={t("reserves.exportarA3")}>
            <Button
                variant="contained"
                onClick={() => download()}
                startIcon={<Icon>download</Icon>}
            >
                {t("reserves.exportarA3")}
            </Button>
        </Tooltip>
    );
};

const TotalsContent = (props) => {
    const { totals } = props;
    const { t } = useTranslation();
    const { currentLanguage } = useBaseAppContext();

    return (
        <Box
            sx={{
                display: "flex",
                justifyContent: "center",
                flexWrap: "wrap",
                gap: 5,
                mt: 1,
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                }}
            >
                <Typography variant="h6">
                    {t("reserves.totals.adults")}
                </Typography>
                <Typography>
                    {numberFormat(totals.adults, null, currentLanguage)}
                </Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                }}
            >
                <Typography variant="h6">
                    {t("reserves.totals.infants")}
                </Typography>
                <Typography>
                    {numberFormat(totals.infants, null, currentLanguage)}
                </Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                }}
            >
                <Typography variant="h6">
                    {t("reserves.totals.bebes")}
                </Typography>
                <Typography>
                    {numberFormat(totals.bebes, null, currentLanguage)}
                </Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                }}
            >
                <Typography variant="h6">
                    {t("reserves.totals.efectiu")}
                </Typography>
                <Typography>
                    {numberFormatCurrency(
                        totals.importEfectiu,
                        "eur",
                        null,
                        currentLanguage,
                    )}
                </Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                }}
            >
                <Typography variant="h6">
                    {t("reserves.totals.targeta")}
                </Typography>
                <Typography>
                    {numberFormatCurrency(
                        totals.importTargeta,
                        "eur",
                        null,
                        currentLanguage,
                    )}
                </Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                }}
            >
                <Typography variant="h6">
                    {t("reserves.totals.reserves")}
                </Typography>
                <Typography>
                    {numberFormat(totals.totalReserves, null, currentLanguage)}
                </Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                }}
            >
                <Typography variant="h6">
                    {t("reserves.totals.total")}
                </Typography>
                <Typography>
                    {numberFormatCurrency(
                        totals.importTotal,
                        "eur",
                        null,
                        currentLanguage,
                    )}
                </Typography>
            </Box>
        </Box>
    );
};

const FilterContent = (props) => {
    const { setNamedQueries } = props;
    const { data } = useFilterContext();

    const buildNamedQueries = React.useMemo(
        () => [
            `agendaProducteNom#${data?.agendaProducte}`,
            `numero#${data?.numero}`,
            `producteId#${data?.producte?.id}`,
            `zonaId#${data?.zona?.id}`,
            `dataReservaInici#${data?.dataReservaInici}`,
            `dataReservaFi#${data?.dataReservaFi}`,
            `dataInici#${data?.dataInici}`,
            `dataFi#${data?.dataFi}`,
            `dataDisponibilitatInici#${data?.dataDisponibilitatInici}`,
            `dataDisponibilitatFi#${data?.dataDisponibilitatFi}`,
            `pagamentTipus#${data?.pagamentTipus}`,
            `tipus#${data?.tipus}`,
            `userId#${data?.user?.id}`,
            `recursId#${data?.recurs?.id}`,
            `primerRecursId#${data?.primerRecurs?.id}`,
            `estat#${data?.estat}`,
            `estatEveret#${data?.estatEveret}`,
            `errorEveret#${data?.errorEveret}`,
        ],
        [data],
    );
    React.useEffect(() => {
        setNamedQueries(buildNamedQueries);
    }, [buildNamedQueries]);

    return (
        <MuiGrid container spacing={2}>
            <GridFilterField
                name="agendaProducte"
                sm={6}
                md={6}
                lg={3}
                xl={3}
                advancedSearchColumns={columnesAgendaProducte}
                advancedSearchMaxWidth="lg"
            />
            <GridFilterField name="numero" sm={3} md={3} lg={1.5} xl={1.5} />
            <GridFilterField
                name="producte"
                sm={5}
                md={5}
                lg={2.5}
                xl={2.5}
                advancedSearchColumns={columnesProducte}
                advancedSearchMaxWidth="lg"
            />
            <GridFilterField
                name="zona"
                sm={4}
                md={4}
                lg={2}
                xl={2}
                advancedSearchColumns={columnesZones}
                advancedSearchMaxWidth="sm"
            />
            <GridFilterField
                name="pagamentTipus"
                sm={6}
                md={4}
                lg={3}
                xl={1.5}
            />
            <GridFilterField name="tipus" sm={6} md={4} lg={3} xl={1.5} />

            <GridFilterField
                name="dataInici"
                sm={6}
                md={4}
                lg={3}
                xl={2}
                type="date"
            />
            <GridFilterField
                name="dataFi"
                sm={6}
                md={4}
                lg={3}
                xl={2}
                type="date"
            />
            <GridFilterField
                name="dataDisponibilitatInici"
                sm={6}
                md={4}
                lg={3}
                xl={2}
            />
            <GridFilterField
                name="dataDisponibilitatFi"
                sm={6}
                md={4}
                lg={3}
                xl={2}
            />
            <GridFilterField
                name="dataReservaInici"
                sm={6}
                md={4}
                lg={3}
                xl={2}
                type="date"
            />
            <GridFilterField
                name="dataReservaFi"
                sm={6}
                md={4}
                lg={3}
                xl={2}
                type="date"
            />

            <GridFilterField
                name="user"
                sm={6}
                md={6}
                lg={3}
                advancedSearchColumns={columnesUsuaris}
                advancedSearchMaxWidth={"lg"}
            />
            <GridFilterField name="recurs" sm={6} md={6} lg={3} xl={2.25} />
            <GridFilterField
                name="primerRecurs"
                sm={6}
                md={6}
                lg={3}
                xl={2.25}
            />
            <GridFilterField name="estat" sm={6} md={6} lg={3} xl={1.5} />
            <GridFilterField name="estatEveret" sm={6} md={6} lg={3} xl={2.5} />
            <GridFilterField
                name="errorEveret"
                label=""
                title="Error Everet"
                xs={0.5}
                icon={<WarningAmberIcon />}
                checkedIcon={<WarningIcon />}
                color={data?.errorEveret ? "error" : "primary"}
            />

            {/* <MuiGrid item lg={0.5}>
                <Tooltip title={t("comu.resetFiltre")}>
                    <IconButton
                        onClick={() => filterApiRef?.current?.doReset()}
                    >
                        <Icon>filter_alt_off</Icon>
                    </IconButton>
                </Tooltip>
            </MuiGrid> */}
        </MuiGrid>
    );
};

const exportSpringFilterBuilder = (data, setExportFixedFilter) => {
    const { and, equal, ge, gt, le, like, exists, isNotNull } =
        SpringFilterQueryBuilder;

    const filter = and(
        like("agendaProducte.nom", likeAny(data?.agendaProducte)),

        ge(
            "dataReserva",
            data?.dataReservaInici
                ? data?.dataReservaInici + "T00:00:00"
                : undefined,
        ),
        le(
            "dataReserva",
            data?.dataReservaFi ? data?.dataReservaFi + "T23:59:59" : undefined,
        ),

        ge("data", data?.dataInici ? data?.dataInici + "T00:00:00" : undefined),
        le("data", data?.dataFi ? data?.dataFi + "T23:59:59" : undefined),
        ge(
            "dataDisponibilitat",
            data?.dataDisponibilitatInici?.substring(0, 19),
        ),
        le("dataDisponibilitat", data?.dataDisponibilitatFi?.substring(0, 19)),
        like("numero", likeAny(data?.numero)),
        equal("pagamentTipus", data?.pagamentTipus),
        equal("user.id", data?.user?.id),
        equal("producte.id", data?.producte?.id),
        equal("tipus", data?.tipus),
        equal("estat", data?.estat),
        equal("estatEveret", data?.estatEveret),
        data?.errorEveret
            ? equal("errorEveret", data?.errorEveret.toString())
            : undefined,
        equal("zona.id", data?.zona?.id),
        gt("importTotal", 0), // TODO: Aquest filtre no esta en es dos llocs
        equal("primerRecurs.id", data?.primerRecurs?.id),
        data?.recurs &&
            exists(
                equal(
                    "agendaProducte.agendesRecurs.recurs.id",
                    data?.recurs?.id,
                ),
            ),
    ).toString();

    setExportFixedFilter(filter);
};

const springFilterBuilder = (data, setExportFixedFilter) => {
    const { and, equal, ge, le, like, exists, isNotNull } =
        SpringFilterQueryBuilder;

    exportSpringFilterBuilder(data, setExportFixedFilter);

    const filter = and(
        like("agendaProducte.nom", likeAny(data?.agendaProducte)),

        ge(
            "dataReserva",
            data?.dataReservaInici
                ? data?.dataReservaInici + "T00:00:00"
                : undefined,
        ),
        le(
            "dataReserva",
            data?.dataReservaFi ? data?.dataReservaFi + "T23:59:59" : undefined,
        ),

        ge("data", data?.dataInici ? data?.dataInici + "T00:00:00" : undefined),
        le("data", data?.dataFi ? data?.dataFi + "T23:59:59" : undefined),
        ge(
            "dataDisponibilitat",
            data?.dataDisponibilitatInici?.substring(0, 19),
        ),
        le("dataDisponibilitat", data?.dataDisponibilitatFi?.substring(0, 19)),
        like("numero", likeAny(data?.numero)),
        equal("pagamentTipus", data?.pagamentTipus),
        equal("user.id", data?.user?.id),
        equal("producte.id", data?.producte?.id),
        equal("tipus", data?.tipus),
        equal("estat", data?.estat),
        equal("estatEveret", data?.estatEveret),
        data?.errorEveret
            ? equal("errorEveret", data?.errorEveret?.toString())
            : undefined,
        equal("zona.id", data?.zona?.id),
        equal("primerRecurs.id", data?.primerRecurs?.id),
        data?.recurs &&
            exists(
                equal(
                    "agendaProducte.agendesRecurs.recurs.id",
                    data?.recurs?.id,
                ),
            ),
    ).toString();

    return filter;
};

const ReservesFilter = (props) => {
    const {
        filterApiRef,
        onSpringFilterChange,
        onFilterInitialized,
        setExportFixedFilter,
        setNamedQueries,
        isNoTraspassats,
    } = props;

    return (
        <Box sx={{ mb: 2 }}>
            <Filter
                name={"R_FILTER"}
                resourceName={"reserva"}
                persistentState
                springFilterBuilder={(data) =>
                    springFilterBuilder(data, setExportFixedFilter)
                }
                onSpringFilterChange={onSpringFilterChange}
                onFilterInitialized={onFilterInitialized}
                size="small"
                apiRef={filterApiRef}
                initialData={isNoTraspassats ? {} : { estat: "ACTIVA" }}
                initialOnChangeRequest
            >
                <FilterContent
                    filterApiRef={filterApiRef}
                    setNamedQueries={setNamedQueries}
                />
            </Filter>
        </Box>
    );
};

const Reserves = () => {
    const { t } = useTranslation();
    const [fixedFilter, setFixedFilter] = React.useState();
    const [exportFixedFilter, setExportFixedFilter] = React.useState();
    const [filterInitialized, setFilterInitialized] = React.useState(false);
    const [namedQueries, setNamedQueries] = React.useState([]);
    const navigate = useNavigate();
    const gridApiRef = React.useRef();
    const filterApiRef = React.useRef();
    const { currentLanguage } = useBaseAppContext();
    const { apiInitialized, exec: apiExec } = useResourceApi("reserva");
    const [totals, setTotals] = React.useState();
    const [noTraspassats, setNoTraspassats] = React.useState();
    const [isNoTraspassats, setIsNoTraspassats] = React.useState(false);
    const [refreshInitialDataCount, setRefreshInitialDataCount] =
        React.useState(0);
    const [selectionModel, setSelectionModel] = React.useState([]);
    const { and, notEqual, le, or, isNull } = SpringFilterQueryBuilder;

    const dataLimit = new Date();
    dataLimit.setDate(
        dataLimit.getDate() - noTraspassats?.diesAbansTraspasAutomatic,
    );
    const filterNoTraspassats = isNoTraspassats
        ? and(
              or(
                  isNull("estatEveret"),
                  notEqual("estatEveret", "PAGAMENT_CREAT"),
              ),
              le("data", dataLimit.toISOString().split("T")[0] + "T23:59:59"),
          )?.toString()
        : undefined;

    const activeFilters = [fixedFilter, filterNoTraspassats].filter(Boolean);
    const fixedFilterWithNoTraspassats =
        activeFilters.length > 0 ? activeFilters.join(" AND ") : undefined;

    // Hi ha aquesta useEffect perque quan es fa click al boto de no traspassats, es refresca el filtre inicial i es lleven tots el filtres
    // i es reinicia el filtre amb el nou filtre de no traspassats
    // Es fa un contador i no un boolea, perque si es fa click molt ràpidament el boolea pot quedar "igual" i no refrescar el filtre,
    // en canvi amb el contador sempre canvia i refresca el filtre
    React.useEffect(() => {
        if (refreshInitialDataCount > 0) {
            filterApiRef?.current?.doReset();
        }
    }, [refreshInitialDataCount]);

    const activeFiltersExport = [exportFixedFilter, filterNoTraspassats].filter(
        Boolean,
    );
    const exportFixedFilterWithNoTraspassats =
        activeFiltersExport.length > 0
            ? activeFilters.join(" AND ")
            : undefined;

    const columns = [
        {
            name: "dataReserva",
            flex: 3,
            minWidth: 200,
            valueFormatter: ({ value }) => {
                if (!value) return "";

                return value.toString().substring(0, 16).replace("T", " ");
            },
        },
        {
            name: "agendaProducte",
            flex: 3,
            minWidth: 220,
        },
        {
            name: "data",
            flex: 3,
            minWidth: 200,
        },
        {
            name: "numero",
            flex: 3,
            minWidth: 170,
        },
        {
            name: "primerRecurs",
            flex: 3,
            minWidth: 140,
        },
        {
            name: "adults",
            flex: 1.5,
            minWidth: 90,
        },
        {
            name: "infants",
            flex: 1.5,
            minWidth: 90,
        },
        {
            name: "bebes",
            flex: 1.5,
            minWidth: 90,
        },
        // {
        //     name: "pagamentTipus",
        //     flex: 2,
        //     minWidth: 130,
        // },
        {
            name: "importDescomptat",
            flex: 3,
            minWidth: 150,
            type: "currency",
            currencyDecimalPlaces: 2,
            formFieldProps: {
                suffix: " €",
            },
            valueFormatter: (params) => suffixValueFormatter(params),
        },
        {
            name: "importEfectiu",
            flex: 3,
            minWidth: 150,
            type: "currency",
            currencyDecimalPlaces: 2,
            formFieldProps: {
                suffix: " €",
            },
            valueFormatter: (params) => suffixValueFormatter(params),
        },
        {
            name: "importTargeta",
            flex: 3,
            minWidth: 150,
            type: "currency",
            currencyDecimalPlaces: 2,
            formFieldProps: {
                suffix: " €",
            },
            valueFormatter: (params) => suffixValueFormatter(params),
        },
        {
            name: "importBase",
            flex: 2.5,
            minWidth: 150,
            type: "currency",
            currencyDecimalPlaces: 2,
            formFieldProps: {
                suffix: " €",
            },
            valueFormatter: (params) => suffixValueFormatter(params),
        },
        {
            name: "quotaIva",
            flex: 2.5,
            minWidth: 150,
            type: "currency",
            currencyDecimalPlaces: 2,
            formFieldProps: {
                suffix: " €",
            },
            valueFormatter: (params) => suffixValueFormatter(params),
        },
        {
            name: "importTotal",
            flex: 2.5,
            minWidth: 150,
            type: "currency",
            currencyDecimalPlaces: 2,
            formFieldProps: {
                suffix: " €",
            },
            valueFormatter: (params) => suffixValueFormatter(params),
        },
        {
            name: "user",
            flex: 3,
            minWidth: 150,
        },
        {
            name: "zona",
            flex: 3,
            minWidth: 150,
        },
        {
            name: "estat",
            flex: 2,
            minWidth: 120,
        },
        {
            name: "estatEveret",
            flex: 2,
            minWidth: 160,
        },
    ];

    const toolbarAdditionalActions = () => {
        const listActions = [
            {
                position: 2,
                component: (
                    <Tooltip
                        title={
                            isNoTraspassats
                                ? t("reserves.noTraspassats.aplicat")
                                : t("reserves.noTraspassats.noAplicat")
                        }
                    >
                        <IconButton
                            color={isNoTraspassats ? "warning" : undefined}
                            onClick={() => {
                                setIsNoTraspassats(!isNoTraspassats);
                                if (!isNoTraspassats) {
                                    setRefreshInitialDataCount(
                                        refreshInitialDataCount + 1,
                                    );
                                }
                            }}
                            sx={{ mr: 1 }}
                        >
                            <Badge
                                badgeContent={noTraspassats?.totalNoTraspassats}
                                color="info"
                                max={999}
                                sx={{
                                    "& .MuiBadge-badge": {
                                        height: 16,
                                        minWidth: 16,
                                        fontSize: "0.7rem",
                                        padding: "0 6px",
                                    },
                                }}
                            >
                                <ReportProblemIcon />
                            </Badge>
                        </IconButton>
                    </Tooltip>
                ),
            },
            {
                position: 2,
                component: (
                    <Tooltip title={t("comu.resetFiltre")}>
                        <IconButton
                            onClick={() => filterApiRef?.current?.doReset()}
                        >
                            <Icon>filter_alt_off</Icon>
                        </IconButton>
                    </Tooltip>
                ),
            },
            {
                position: 2,
                component: (
                    <ActionButton
                        resourceName="reserva"
                        code="R_SELECCIONAR_TOT"
                        title={t("reserves.seleccionarTot")}
                        detached
                        variant="contained"
                        icon="checklist"
                        formDialogSize="lg"
                        ignoreFields
                        confirmed
                        formStaticData={{
                            springFilter: fixedFilterWithNoTraspassats,
                        }}
                        onSuccess={(params) => {
                            setSelectionModel(
                                params.map((value) => parseInt(value)),
                            );
                        }}
                    />
                ),
            },
            {
                position: 2,
                component: (
                    <IconButton
                        title={t("comu.deselecionar")}
                        onClick={() => gridApiRef?.current?.doSelectNone()}
                        disabled={selectionModel?.length === 0}
                    >
                        <Icon>playlist_remove</Icon>
                    </IconButton>
                ),
            },
            {
                position: 2,
                component: (
                    <ActionButton
                        code="R_ACCIO_FACTURAR_I_PAGAR"
                        resourceName="reserva"
                        multiple
                        icon="paid"
                        variant="contained"
                        onSuccess={() => {
                            gridApiRef?.current?.doRefresh();
                        }}
                    />
                ),
            },
            {
                position: 7,
                component: (
                    <BotoExportacio
                        resourceName={"reserva"}
                        exportData={exportData}
                        // exportCallbacks={exportCallbacks}
                    />
                ),
            },
        ];

        return listActions;
    };
    const rowAdditionalActions = (params) => {
        const noPotFacturar = ["FACTURA_CREADA", "PAGAMENT_CREAT"].includes(
            params?.estatEveret,
        );

        const potEfectuarPagament = params?.estatEveret === "FACTURA_CREADA";

        const listActions = [
            {
                icon: "search",
                label: t("comu.detall"),
                onClick: () => navigate(`form/${params?.id}`),
            },

            // TODO: Descomentar si es vol tornar a activar l'acció d'abonar
//             {
//                 icon: "currency_exchange",
//                 showInMenu: true,
//                 rowLinkAction: "R_ABONAR",
//                 rowLinkActionOnSuccess: () => gridApiRef?.current?.doRefresh(),
//                 rowLinkFormChildren: (
//                     <ActionAbonarContent reservaId={params.id} />
//                 ),
//                 rowLinkFormStaticData: { reservaId: params.id },
//                 rowLinkFormDialogSize: "xl",
//                 rowLinkFormInitialOnChangeRequest: true,
//                 disabled:
//                     params?.abonamentTipus === "ABONAMENT_TOTAL" ||
//                     params?.abonamentTipus === "ABONAMENT_PARCIAL" ||
//                     params?.abonamentTipus === "OPERACIO_ABONADA_TOTAL",
//             },
			// TODO: Descomentar si es vol tornar a activar l'acció d'anul·lar
//            {
//                icon: "lock",
//                showInMenu: true,
//                rowLinkAction: "R_ANULAR",
//                rowLinkActionOnSuccess: () => obtenirTotals(),
//                disabled: params?.estatEveret === "FACTURA_CREADA",
//            },
            // TODO: Descomentar si es vol tornar a activar l'acció d'activar
//            {
//                icon: "lock_open",
//                showInMenu: true,
//                rowLinkAction: "R_ACTIVAR",
//                rowLinkActionOnSuccess: () => obtenirTotals(),
//                disabled: params?.estat === "ACTIVA",
//            },

            {
                icon: "receipt_long",
                showInMenu: true,
                rowLinkAction: "R_FACTURAR",
                disabled: noPotFacturar,
            },
            {
                icon: "payments",
                showInMenu: true,
                rowLinkAction: "R_EFECTUAR_PAGAMENT",
                // rowLinkActionOnSuccess: () => obtenirTotals(),
                rowLinkActionIgnoreFields: true,
                disabled: !potEfectuarPagament,
            },
        ];

        return listActions;
    };

    React.useEffect(() => {
        gridApiRef?.current?.doRefresh();
    }, [currentLanguage]);

    const obtenirNoTraspassats = () => {
        if (apiInitialized) {
            apiExec("exec_R_OBTENIR_NO_TRASPASSATS", {
                callbacks: {
                    setResponse: (response) => {
                        setNoTraspassats(response.data);
                    },
                    setError: (error) => {},
                },
            });
        }
    };

    const obtenirTotals = () => {
        if (apiInitialized) {
            apiExec("exec_R_OBTENIR_TOTALS", {
                data: {
                    springFilter:
                        fixedFilterWithNoTraspassats ?? "(estat : 'ACTIVA')",
                },
                callbacks: {
                    setResponse: (response) => {
                        const resource = {
                            ...response.data,
                            _resource: response,
                            _links: response.links,
                        };
                        setTotals(resource);
                    },
                    setError: (error) => {},
                },
            });
        }
    };

    React.useEffect(() => {
        if (apiInitialized && filterInitialized) {
            obtenirTotals();
            obtenirNoTraspassats();
        }
    }, [
        apiInitialized,
        filterInitialized,
        fixedFilterWithNoTraspassats,
    ]);

    const exportData = {
        page: "UNPAGED",
        perspective: ["excel"],
        type: "ODS",
        filter: exportFixedFilterWithNoTraspassats,
        field: [
            "refFactura",
            "dataFacturacio",
            "tipusIva",
            "id",
            "baseImposable",
            "importIva",
            "importReserva",
            "idProfessional",
            "raoSocial",
            "codiComptableClient",
        ],
    };

    return (
        <Box
            sx={{
                height: "100%",
                "& .cellWarning": {
                    bgcolor: (theme) => getFielYellow(theme.palette.mode),
                },
                "& .MuiDataGrid-row:hover .cellWarning": {
                    bgcolor: (theme) =>
                        `${darken(
                            getFielYellow(theme.palette.mode),
                            0.1,
                        )}!important`,
                },
                "& .cellAbonat": {
                    bgcolor: (theme) => getFielYellow(theme.palette.mode),
                },
                "& .MuiDataGrid-row:hover .cellAbonat": {
                    bgcolor: (theme) =>
                        `${darken(
                            getFielYellow(theme.palette.mode),
                            0.1,
                        )}!important`,
                },
                "& .cellAnulada": {
                    bgcolor: (theme) =>
                        getFieldHighlightOrange(theme.palette.mode),
                },
                "& .MuiDataGrid-row:hover .cellAnulada": {
                    bgcolor: (theme) =>
                        `${darken(
                            getFieldHighlightOrange(theme.palette.mode),
                            0.1,
                        )}!important`,
                },
                "& .cellErrorEveret": {
                    bgcolor: (theme) =>
                        getFieldHighlightRed(theme.palette.mode),
                },
                "& .MuiDataGrid-row:hover .cellErrorEveret": {
                    bgcolor: (theme) =>
                        `${darken(
                            getFieldHighlightRed(theme.palette.mode),
                            0.1,
                        )}!important`,
                },
                "& .cellAbonament": {
                    bgcolor: (theme) =>
                        getFieldHighlightRed(theme.palette.mode),
                    color: "#ab0000",
                },
                "& .MuiDataGrid-row:hover .cellAbonament": {
                    bgcolor: (theme) =>
                        `${darken(
                            getFieldHighlightRed(theme.palette.mode),
                            0.05,
                        )}!important`,
                },
            }}
        >
            <Grid
                apiRef={gridApiRef}
                title={t("reserves.title")}
                resourceName="reserva"
                columns={columns}
                namedQueries={namedQueries}
                selectable
                actionExecEnabled
                selectionModel={selectionModel}
                onSelectionChange={setSelectionModel}
                formRoutePath="form"
                density="compact"
                findDisabled={!filterInitialized}
                fixedFilter={fixedFilterWithNoTraspassats}
                toolbarHideQuickFilter
                onRefresh={() => obtenirTotals()}
                onDelete={() => obtenirTotals()}
                toolbarAdditionalActions={toolbarAdditionalActions()}
                toolbarAdditionalRow={
                    <>
                        <ReservesFilter
                            filterApiRef={filterApiRef}
                            onSpringFilterChange={setFixedFilter}
                            onFilterInitialized={() =>
                                setFilterInitialized(true)
                            }
                            setNamedQueries={setNamedQueries}
                            setExportFixedFilter={setExportFixedFilter}
                            isNoTraspassats={isNoTraspassats}
                        />
                        {totals && (
                            <Box sx={{ mb: 2 }}>
                                <TotalsContent totals={totals} />
                            </Box>
                        )}
                    </>
                }
                rowHideEditViewAction
                rowAdditionalActions={(params) =>
                    rowAdditionalActions(params.row)
                }
                initialState={{
                    pinnedColumns: {
                        left: [GRID_CHECKBOX_SELECTION_FIELD],
                        right: ["actions"],
                    },
                }}
                defaultSortModel={[
                    {
                        field: "data",
                        sort: "desc",
                    },
                ]}
                getCellClassName={({ row, field }) => {
                    // Pinta les caselles de les operacions que son abonaments totals/parcials
                    if (
                        row?.abonamentTipus === "ABONAMENT_TOTAL" ||
                        row?.abonamentTipus === "ABONAMENT_PARCIAL"
                    ) {
                        if (field === "importTotal" && row?.importTotal < 0) {
                            return "cellAbonament";
                        }
                        if (
                            field === "importEfectiu" &&
                            row?.importEfectiu < 0
                        ) {
                            return "cellAbonament";
                        }
                        if (
                            field === "importTargeta" &&
                            row?.importTargeta < 0
                        ) {
                            return "cellAbonament";
                        }
                    }

                    // Pinta la linia de l'operacio original que ha estat total/parcialment abonada
                    if (
                        row?.abonamentTipus === "OPERACIO_ABONADA_PARCIAL" ||
                        row?.abonamentTipus === "OPERACIO_ABONADA_TOTAL"
                    ) {
                        return "cellAbonat";
                    }

                    if (row?.estat === "ANULADA") {
                        return "cellAnulada";
                    }
                    if (row?.errorEveret) {
                        return "cellErrorEveret";
                    }
                    return row?.entradaManual ||
                        row?.liniaManual ||
                        row?.observacions
                        ? "cellWarning"
                        : "";
                }}
            />
        </Box>
    );
};

export default Reserves;
